import React, { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";

interface PageToolbarContextValue {
	toolbar: ReactNode | null;
	setToolbar: (node: ReactNode | null) => void;
}

const PageToolbarStateContext = createContext<ReactNode | null>(null);
const PageToolbarSetContext = createContext<((node: ReactNode | null) => void) | null>(null);

export const PageToolbarProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
	const [toolbar, setToolbarState] = useState<ReactNode | null>(null);

	const setToolbar = useCallback((node: ReactNode | null) => {
		setToolbarState(prev => (prev === node ? prev : node));
	}, []);

	return (
		<PageToolbarSetContext.Provider value={setToolbar}>
			<PageToolbarStateContext.Provider value={toolbar}>{children}</PageToolbarStateContext.Provider>
		</PageToolbarSetContext.Provider>
	);
};

/** Stable setter for layout effects — avoids re-running when toolbar content updates. */
export const useSetPageToolbar = (): ((node: ReactNode | null) => void) | null =>
	useContext(PageToolbarSetContext);

export const usePageToolbar = (): PageToolbarContextValue | null => {
	const toolbar = useContext(PageToolbarStateContext);
	const setToolbar = useContext(PageToolbarSetContext);

	return useMemo(() => {
		if (!setToolbar) return null;
		return { toolbar, setToolbar };
	}, [toolbar, setToolbar]);
};
